import React, { useRef, useState } from "react";
import Avatar from "../../../components/common/Avatar";
import WeekBar from "../people/WeekBar";
import ResourceTypeIcon from "../people/ResourceTypeIcon";
import { dayName, diffDays, isWeekend } from "../../../utils/weekDates";
import { fmtHours } from "../timesheets/timesheetsConfig";
import { abbreviate, moveDates, workingDays } from "./scheduleConfig";
import styles from "./ScheduleUI.module.css";

const cellAt = (x, y) => {
  const el = document.elementsFromPoint(x, y).find((e) => e.dataset && e.dataset.date && e.dataset.row);
  return el ? { row: el.dataset.row, date: el.dataset.date } : null;
};

const tint = (hex) => (/^#[0-9a-f]{6}$/i.test(hex || "") ? `${hex}1f` : "#eef2f7");

function Block({ item, first, last, span, draggable, ghost, dragging, onMouseDown, onOpen }) {
  const s = Math.max(0, diffDays(first, item.start));
  const e = Math.min(span - 1, diffDays(first, item.end));
  const style = { gridColumn: `${s + 1} / ${e + 2}`, gridRow: item.lane + 1 };
  const days = workingDays(item.start < first ? first : item.start, item.end > last ? last : item.end);
  const narrow = days <= 1;

  if (item.kind === "leave") {
    const label = narrow ? "Leave" : item.type_name || "Leave";
    return (
      <div
        role="button"
        tabIndex={0}
        style={style}
        className={`${styles.block} ${item.approved ? styles.leave : styles.leavePending}`}
        title={`${item.employee_name || "Leave"}: ${item.type_name || "Leave"} (${item.approved ? "approved" : "pending approval"})`}
        onClick={() => onOpen(item)}
        onKeyDown={(ev) => ev.key === "Enter" && onOpen(item)}
      >
        <span className={styles.blockName}>{label}</span>
        {!item.approved && !narrow && <span className={styles.blockHours}>Pending</span>}
      </div>
    );
  }

  const problem = item.clash ? `Double-booked with ${item.clash}` : item.leaveClash ? "Overlaps approved leave" : null;
  const hours = fmtHours(item.perDay);
  const color = item.project_color || null;
  return (
    <div
      role="button"
      tabIndex={0}
      style={{ ...style, ...(color ? { borderColor: color, background: tint(color) } : null) }}
      className={`${styles.block} ${color ? "" : styles.internal} ${problem ? styles.problem : ""} ${draggable ? styles.movable : ""} ${ghost ? styles.ghost : ""} ${dragging ? styles.dragging : ""}`}
      title={`${item.title}${item.activity_type_name ? ` · ${item.activity_type_name}` : ""} · ${hours} a day${problem ? ` · ${problem}` : ""}`}
      onMouseDown={onMouseDown}
      onClick={draggable ? undefined : () => onOpen(item)}
      onKeyDown={(ev) => ev.key === "Enter" && onOpen(item)}
    >
      {problem && <i className={styles.warn} aria-label={problem}>!</i>}
      <span className={styles.blockName}>{narrow ? abbreviate(item.title) : item.title}</span>
      <span className={styles.blockHours}>{hours}</span>
    </div>
  );
}

// The people-by-days grid. Drag across empty days to book, drag a booking to move it, click one to edit it.
export default function ScheduleGrid({
  groups, dates, today, selection, collapsed, onToggleCollapse,
  canCreate, canMove, onCreate, onMove, onBooking, onLeave, onPerson,
}) {
  const [create, setCreate] = useState(null);
  const [drag, setDrag] = useState(null);
  const createRef = useRef(null);
  const dragRef = useRef(null);

  const first = dates[0];
  const last = dates[dates.length - 1];
  const rowByKey = Object.fromEntries(groups.flatMap((g) => g.rows).map((r) => [r.key, r]));
  const allKeys = Object.keys(rowByKey);
  const columns = dates.map((d) => (isWeekend(d) ? "minmax(10px, 0.3fr)" : "minmax(42px, 1fr)")).join(" ");

  const startCreate = (ev, row, date) => {
    if (!canCreate || ev.button !== 0 || isWeekend(date)) return;
    ev.preventDefault();
    createRef.current = { row: row.key, a: date, b: date };
    setCreate(createRef.current);
    const move = (m) => {
      const hit = cellAt(m.clientX, m.clientY);
      if (hit && hit.row === row.key) {
        createRef.current = { ...createRef.current, b: hit.date };
        setCreate(createRef.current);
      }
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      const { a, b } = createRef.current;
      createRef.current = null;
      setCreate(null);
      onCreate(row, a <= b ? a : b, a <= b ? b : a);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const startDrag = (ev, item, row) => {
    if (!canMove || ev.button !== 0) return;
    const origin = { x: ev.clientX, y: ev.clientY };
    dragRef.current = { item, row, grab: cellAt(ev.clientX, ev.clientY)?.date || item.start, active: false, target: null };
    const move = (m) => {
      const s = dragRef.current;
      if (!s.active && Math.hypot(m.clientX - origin.x, m.clientY - origin.y) < 5) return;
      s.active = true;
      const hit = cellAt(m.clientX, m.clientY);
      s.target = hit && rowByKey[hit.row]?.kind === row.kind ? hit : null;
      setDrag({ ...s });
    };
    const up = () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("mouseup", up);
      const s = dragRef.current;
      dragRef.current = null;
      setDrag(null);
      if (!s.active) onBooking(item);
      else if (s.target) onMove(item, rowByKey[s.target.row], s.grab, s.target.date);
    };
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
  };

  const ghostFor = (row) => {
    if (!drag?.target || drag.target.row !== row.key) return null;
    const { start, end } = moveDates(drag.item, drag.grab, drag.target.date);
    return { ...drag.item, start, end, lane: 0 };
  };

  const rowChecked = (row) => selection.selectedIds.has(row.key);
  const allChecked = allKeys.length > 0 && allKeys.every((k) => selection.selectedIds.has(k));

  return (
    <div className={styles.boardWrap}>
      <div className={styles.board} style={{ "--cols": columns }}>
        <div className={`${styles.line} ${styles.head}`}>
          <div className={styles.who}>
            <input type="checkbox" aria-label="Select everyone" checked={allChecked} onChange={(e) => selection.toggleAll(allKeys, e.target.checked)} />
            <span className={styles.headLabel}>People &amp; resources</span>
          </div>
          <div className={styles.dayHeads}>
            {dates.map((d) => (
              <div key={d} className={`${styles.dayHead} ${isWeekend(d) ? styles.weekend : ""} ${d === today ? styles.today : ""}`}>
                {isWeekend(d) ? <span>{Number(d.slice(8))}</span> : <><span>{dayName(d)}</span><strong>{Number(d.slice(8))}</strong></>}
              </div>
            ))}
          </div>
        </div>

        {groups.map((g) => {
          const keys = g.rows.map((r) => r.key);
          const open = !collapsed[g.key];
          return (
            <section key={g.key}>
              {g.label && (
                <div className={`${styles.line} ${styles.groupLine}`}>
                  <div className={styles.groupCell}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${g.label}`}
                      checked={keys.every((k) => selection.selectedIds.has(k))}
                      onChange={(e) => selection.toggleGroup(keys, e.target.checked)}
                    />
                    <button type="button" className={styles.chevron} aria-label={open ? `Collapse ${g.label}` : `Expand ${g.label}`} aria-expanded={open} onClick={() => onToggleCollapse(g.key)}>
                      {open ? "▾" : "▸"}
                    </button>
                    <i className={styles.dot} style={{ background: g.color }} />
                    <strong>{g.label}</strong>
                    <span className={styles.count}>{g.rows.length}</span>
                    {g.isResource ? (
                      g.clashing && <span className={styles.groupBad}>Double-booking</span>
                    ) : (
                      <span className={g.overbooked > 0 ? styles.groupBad : styles.groupStats}>
                        {fmtHours(g.booked)} of {fmtHours(g.capacity)} booked
                        {g.overbooked > 0 && ` · ${g.overbooked} overbooked`}
                      </span>
                    )}
                  </div>
                </div>
              )}
              {open && g.rows.map((row) => {
                const ghost = ghostFor(row);
                const selecting = create?.row === row.key ? [create.a, create.b].sort() : null;
                const rowCount = row.laneCount + (row.markers.length ? 1 : 0);
                const { who } = row;
                return (
                  <div key={row.key} className={`${styles.line} ${styles.row} ${rowChecked(row) ? styles.rowSelected : ""}`}>
                    <div className={styles.who}>
                      <input type="checkbox" aria-label={`Select ${who.name}`} checked={rowChecked(row)} onChange={() => selection.toggleRow(row.key)} />
                      {row.kind === "person" ? (
                        <Avatar name={who.name} imageUrl={who.avatar_url} round variant={who.kind === "ghost" ? "contractor" : "default"} />
                      ) : (
                        <span className={styles.resourceIcon}><ResourceTypeIcon type={who.type} /></span>
                      )}
                      <span className={styles.whoText}>
                        {row.kind === "person" ? (
                          <button type="button" className={styles.whoName} onClick={() => onPerson(who)}>{who.name}</button>
                        ) : (
                          <span className={styles.whoName}>{who.name}</span>
                        )}
                        {row.kind === "person" ? (
                          <WeekBar booked={row.booked} capacity={row.capacity} />
                        ) : (
                          <span className={styles.whoSub}>{row.bookedDays} {row.bookedDays === 1 ? "day" : "days"} booked</span>
                        )}
                      </span>
                    </div>
                    <div className={styles.days} style={{ gridTemplateRows: `repeat(${rowCount}, var(--lane))` }}>
                      {dates.map((d, i) => (
                        <div
                          key={d}
                          data-date={d}
                          data-row={row.key}
                          style={{ gridColumn: i + 1, gridRow: `1 / span ${rowCount}` }}
                          className={`${styles.cell} ${isWeekend(d) ? styles.weekend : ""} ${d === today ? styles.today : ""} ${row.overDays.has(d) ? styles.overCell : ""} ${selecting && selecting[0] <= d && d <= selecting[1] ? styles.picking : ""}`}
                          onMouseDown={(ev) => startCreate(ev, row, d)}
                        />
                      ))}
                      {row.items.map((item) => (
                        <Block
                          key={`${item.kind}${item.id || item.leave_request_id}`}
                          item={item}
                          first={first}
                          last={last}
                          span={dates.length}
                          draggable={canMove && item.kind === "booking"}
                          dragging={drag?.active && drag.item.id === item.id && item.kind === "booking"}
                          onMouseDown={(ev) => startDrag(ev, item, row)}
                          onOpen={item.kind === "leave" ? onLeave : onBooking}
                        />
                      ))}
                      {row.markers.map((m) => (
                        <span key={`${m.date}${m.name}`} className={styles.marker} title={`Milestone due: ${m.name}`} style={{ gridColumn: diffDays(first, m.date) + 1, gridRow: rowCount }}>MS</span>
                      ))}
                      {ghost && <Block item={ghost} first={first} last={last} span={dates.length} ghost onOpen={() => {}} />}
                    </div>
                  </div>
                );
              })}
            </section>
          );
        })}
      </div>
      <div className={styles.legend}>
        <span><i className={`${styles.swatch} ${styles.swProject}`} /> Project</span>
        <span><i className={`${styles.swatch} ${styles.swInternal}`} /> Internal</span>
        <span><i className={`${styles.swatch} ${styles.swLeave}`} /> Leave</span>
        <span><i className={`${styles.swatch} ${styles.swPending}`} /> Leave pending</span>
        <span><i className={`${styles.swatch} ${styles.swOver}`} /> Over 8h</span>
        <span><i className={styles.markerKey}>MS</i> Deadline</span>
        <span className={styles.hint}>Drag across empty days to book · drag a booking to move it · click to edit</span>
      </div>
    </div>
  );
}
